import { useState } from "react"
import { Star } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip"
import { useMarketOrNull } from "@/lib/useMarket"
import { bookmark_path, bookmarks_path } from "@/lib/routes"
import { sendJson } from "@/pages/ProductNodes/components/api"
import { TOP_BAR_CELL } from "../topBarCell"
import BookmarkBox from "./BookmarkBox"
import { type Bookmark, setBookmarks, useBookmarks, useCurrentAddress } from "./useBookmarks"

type Reply = { bookmarks: Bookmark[]; bookmark?: Bookmark }

// The star at the right of the top bar, like Chrome's. On a page that is not
// bookmarked, pressing it bookmarks the page at once and opens "Bookmark
// added" to rename it or add a description. On a bookmarked page (blue star)
// it opens "Edit bookmark". Closing the box any way (Done, ✕, Esc, a click
// outside) saves what was typed; the box stays open if that save fails.
export default function BookmarkStar() {
  const address = useCurrentAddress()
  const bookmarks = useBookmarks()
  const market = useMarketOrNull()
  const saved = bookmarks.find((b) => b.url === address)

  const [open, setOpen] = useState(false)
  const [added, setAdded] = useState(false)
  const [editing, setEditing] = useState<Bookmark | null>(null)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [errors, setErrors] = useState<string[]>([])

  const start = (bookmark: Bookmark | null, isNew: boolean) => {
    setEditing(bookmark)
    setAdded(isNew)
    setName(bookmark?.name ?? defaultName(market?.name))
    setDescription(bookmark?.description ?? "")
    setErrors([])
    setOpen(true)
  }

  const add = async () => {
    const nameNow = defaultName(market?.name)
    start(null, true)
    const res = await sendJson<Reply>(bookmarks_path(), "POST", { url: address, name: nameNow })
    if (!res.ok || !res.data?.bookmark) return setErrors(res.errors)
    setBookmarks(res.data.bookmarks)
    setEditing(res.data.bookmark)
  }

  const finish = async () => {
    if (!editing) return setOpen(false)
    const changed = name.trim() !== editing.name || (description.trim() || null) !== editing.description
    if (!changed) return setOpen(false)
    const res = await sendJson<Reply>(bookmark_path(editing.id), "PATCH", { name, description })
    if (!res.ok || !res.data) return setErrors(res.errors)
    setBookmarks(res.data.bookmarks)
    setOpen(false)
  }

  const remove = async () => {
    if (!editing) return
    const res = await sendJson<Reply>(bookmark_path(editing.id), "DELETE")
    if (!res.ok || !res.data) return setErrors(res.errors)
    setBookmarks(res.data.bookmarks)
    setOpen(false)
  }

  const press = () => {
    if (open) return void finish()
    if (saved) start(saved, false)
    else void add()
  }

  return (
    <Popover open={open} onOpenChange={(next) => { if (!next) void finish() }}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={saved ? "Edit bookmark" : "Bookmark this page"}
              aria-pressed={Boolean(saved)}
              onClick={(e) => { e.preventDefault(); press() }}
              className={TOP_BAR_CELL}
            >
              <Star
                size={16}
                strokeWidth={2}
                color={saved ? "var(--primary)" : "var(--muted-foreground)"}
                fill={saved ? "var(--primary)" : "none"}
              />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{saved ? "Edit bookmark" : "Bookmark this page"}</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-80 p-4" onOpenAutoFocus={(e) => e.preventDefault()}>
        <BookmarkBox
          heading={added ? "Bookmark added" : "Edit bookmark"}
          name={name}
          description={description}
          errors={errors}
          ready={editing !== null}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          onDone={() => void finish()}
          onRemove={() => void remove()}
        />
      </PopoverContent>
    </Popover>
  )
}

// The page's tab title without " — Lixi Atlas", plus its market when the
// title does not name it already: "Inventory Overview · Vietnam".
function defaultName(marketName: string | undefined): string {
  const title = document.title.replace(/ — Lixi Atlas$/, "")
  return marketName && !title.includes(marketName) ? `${title} · ${marketName}` : title
}
